import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PopoverPosition } from '@mui/material';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { rasterizePdfPageFromDoc } from '../helpers/pdf-rasterize.helpers';
import { reportDesignPersistError } from '../helpers/design-snapshot-persist.helpers';
import { inheritBlueprintCalibrationFromSource } from '@shared/helpers/design/blueprint-calibration.helpers';
import {
  blueprintStoreyAssignmentFromStartLevel,
  createBlueprintUnderlay,
  getBlueprintDrawingScaleRatio,
  getBlueprintStoreyStackSize,
  getDefaultBlueprintStoreyAssignment,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import type {
  BlueprintStoreyAssignment,
  BlueprintUnderlay,
} from '@shared/models/design/blueprint-underlay.model';
import { useFrozenWhileBusy } from './use-frozen-while-busy.hook';
import {
  isAssignmentReady,
  resolvePdfImportWizardPlan,
  withSelectionStoreyDefaults,
  type PdfImportPageDraft,
  type PdfImportPageDrafts,
  type PdfImportPlaceMode,
  type PdfImportWizardStep,
} from '../helpers/pdf-blueprint-import.helpers';
import type { PdfBlueprintUnderlayDialogProps } from '../components/pdf-blueprint-underlay-dialog.types';
import { usePdfDocument } from './use-pdf-document.hook';

/** 1-based start level for ordinal defaults; the bottom storey without one. */
const toStoreyBaseLevel = (level: number | undefined): number =>
  level !== undefined && level >= 1 ? Math.floor(level) : 1;

/** Storeys of the chosen building, or the Design stack when there is no list. */
const getPdfImportBuildingStoreys = (
  buildings: PdfBlueprintUnderlayDialogProps['buildings'],
  buildingId: string | undefined,
  fallback: PdfBlueprintUnderlayDialogProps['storeys'],
): PdfBlueprintUnderlayDialogProps['storeys'] =>
  buildings?.find(({ id }) => id === buildingId)?.storeys ?? fallback;

/**
 * Wizard state machine for {@link PdfBlueprintUnderlayDialog}: PDF load,
 * page selection, storey mapping, and known-length calibration.
 */
export const usePdfBlueprintUnderlayDialog = ({
  open,
  file,
  storeys: designStoreys,
  buildings,
  selectedBuildingId,
  hasExistingUnderlays = false,
  selectedStoreyLevel: designStoreyLevel,
  matchScaleFrom: selectedMatchScaleFrom,
  onClose,
  onPlaceUnderlays,
}: PdfBlueprintUnderlayDialogProps) => {
  const errorHandler = useErrorHandler();
  // Snapshot props for the open-session loader — placing underlays must not
  // re-run init (that flashed step 1 before the dialog closed).
  const errorHandlerRef = useRef(errorHandler);
  errorHandlerRef.current = errorHandler;
  const [buildingId, setBuildingIdState] = useState(selectedBuildingId);
  const selectedBuildingIdRef = useRef(selectedBuildingId);
  selectedBuildingIdRef.current = selectedBuildingId;
  const buildingCount = buildings?.length ?? 1;
  const buildingCountRef = useRef(buildingCount);
  buildingCountRef.current = buildingCount;
  // Storey defaults follow the building the pages are for.
  const storeys = getPdfImportBuildingStoreys(
    buildings,
    buildingId,
    designStoreys,
  );
  // Another building's pages take scale from that building's pages.
  const matchScaleFrom =
    buildingId === selectedBuildingId
      ? selectedMatchScaleFrom
      : buildings?.find((building) => building.id === buildingId)
          ?.matchScaleFrom;
  // The Design storey only means something on its own building.
  const selectedStoreyLevel =
    buildingId === selectedBuildingId ? designStoreyLevel : undefined;
  const selectedStoreyLevelRef = useRef(selectedStoreyLevel);
  selectedStoreyLevelRef.current = selectedStoreyLevel;
  const storeysRef = useRef(storeys);
  storeysRef.current = storeys;
  const [wizardStep, setPdfImportWizardStep] = useState<PdfImportWizardStep>(1);
  /** Set when the import has no question to ask — place as soon as it can. */
  const autoPlaceRef = useRef(false);
  const [thumbContextMenu, setThumbContextMenu] = useState<
    { pageIndex: number; position: PopoverPosition } | undefined
  >(undefined);
  const selectThumbImageRefs = useRef<Map<number, HTMLImageElement>>(new Map());
  const assignThumbImageRefs = useRef<Map<number, HTMLImageElement>>(new Map());
  /** Rendered thumb bitmap height — sizes → / storey card to the page card. */
  const [assignThumbHeights, setAssignThumbHeights] = useState<
    Record<number, number>
  >({});
  const [loading, setLoading] = useState(false);
  const [pageDrafts, setPdfImportPageDrafts] = useState<PdfImportPageDrafts>(
    {},
  );
  const [focusedPageIndex, setFocusedPageIndex] = useState<number | undefined>(
    undefined,
  );
  const [scrollRequestPageIndex, setScrollRequestPageIndex] = useState<
    number | undefined
  >(undefined);
  const [scrollRequestId, setScrollRequestId] = useState(0);
  const thumbItemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  /** True when the PDF had a single page and step 1 was skipped on open. */
  const [skippedSelectStep, setSkippedSelectStep] = useState(false);

  const { loadedPdf, thumbnails, loadingThumbnails, sessionId } =
    usePdfDocument({
      open,
      file,
      onError: (error) => errorHandlerRef.current(error),
    });

  // Another building has pages when it has a page to take scale from.
  const hasBuildingUnderlays =
    buildingId === selectedBuildingId
      ? hasExistingUnderlays
      : matchScaleFrom !== undefined;
  const canInheritCalibrationLive =
    hasBuildingUnderlays && Boolean(matchScaleFrom?.calibration?.primary);
  const canInheritCalibration = useFrozenWhileBusy(
    canInheritCalibrationLive,
    loading,
  );

  /** Storey levels the project actually has — caps every Storey dropdown. */
  const storeyLevelCount = useMemo(
    () => getBlueprintStoreyStackSize(storeys),
    [storeys],
  );

  /** Base 1-based storey for multi-page ordinal defaults (current Design storey). */
  const storeyBaseLevel = toStoreyBaseLevel(selectedStoreyLevel);

  /**
   * Default storey assignment for a newly selected page — current Design storey.
   */
  const resolveDefaultAssignment = useCallback(
    (): BlueprintStoreyAssignment =>
      getDefaultBlueprintStoreyAssignment({
        underlays: [],
        selectedStoreyLevel,
        storeys,
      }),
    [selectedStoreyLevel, storeys],
  );

  /** Inherit an existing sheet's scale when there is one; else measure after. */
  const finishImportMode: PdfImportPlaceMode = canInheritCalibration
    ? 'inherit'
    : 'measure';

  // Reset wizard chrome when a new PDF session starts, then skip the select
  // step for single-page files.
  useEffect(() => {
    if (!open) return;
    setPdfImportWizardStep(1);
    setSkippedSelectStep(false);
    setFocusedPageIndex(undefined);
    setScrollRequestPageIndex(undefined);
    setScrollRequestId(0);
    setPdfImportPageDrafts({});
    setBuildingIdState(selectedBuildingIdRef.current);
    autoPlaceRef.current = false;
  }, [file, open]);

  useEffect(() => {
    if (!open || sessionId === 0) return;
    const thumbs = thumbnails;
    const first = thumbs[0];
    setFocusedPageIndex(first?.pageIndex);
    const storeyLevelAtOpen = selectedStoreyLevelRef.current;
    const storeysAtOpen = storeysRef.current;

    const assignment = getDefaultBlueprintStoreyAssignment({
      underlays: [],
      selectedStoreyLevel: storeyLevelAtOpen,
      storeys: storeysAtOpen,
    });

    // One page: nothing to select. Place on the Design storey, or ask which
    // building when there are several.
    if (thumbs.length === 1 && first) {
      setPdfImportPageDrafts({
        [first.pageIndex]: { selected: true, assignment },
      });
      setSkippedSelectStep(true);
      if (buildingCountRef.current > 1) {
        setPdfImportWizardStep(2);
      } else {
        autoPlaceRef.current = true;
      }
      return;
    }

    // Multi-page: user picks pages — nothing is pre-selected.
    setPdfImportPageDrafts({});
  }, [open, sessionId, thumbnails]);

  /**
   * Choose the building the pages are for. Selected pages get that building's
   * storey defaults, since levels differ between buildings.
   */
  const setBuildingId = useCallback(
    (nextBuildingId: string) => {
      setBuildingIdState(nextBuildingId);
      const nextStoreys = getPdfImportBuildingStoreys(
        buildings,
        nextBuildingId,
        designStoreys,
      );
      const nextStoreyLevel =
        nextBuildingId === selectedBuildingId ? designStoreyLevel : undefined;
      setPdfImportPageDrafts((prev) =>
        withSelectionStoreyDefaults(
          prev,
          Object.entries(prev)
            .filter(([, draft]) => draft.selected)
            .map(([key]) => Number(key)),
          getDefaultBlueprintStoreyAssignment({
            underlays: [],
            selectedStoreyLevel: nextStoreyLevel,
            storeys: nextStoreys,
          }),
          toStoreyBaseLevel(nextStoreyLevel),
          getBlueprintStoreyStackSize(nextStoreys),
        ),
      );
    },
    [buildings, designStoreyLevel, designStoreys, selectedBuildingId],
  );

  const getDraft = useCallback(
    (pageIndex: number): PdfImportPageDraft =>
      pageDrafts[pageIndex] ?? {
        selected: false,
        assignment: resolveDefaultAssignment(),
      },
    [pageDrafts, resolveDefaultAssignment],
  );

  /**
   * Set whether a page is selected for import.
   * Multi-select remaps storeys from the current Design storey by page order.
   */
  const setPageSelected = useCallback(
    (pageIndex: number, selected: boolean) => {
      setPdfImportPageDrafts((prev) => {
        const current = prev[pageIndex] ?? {
          selected: false,
          assignment: resolveDefaultAssignment(),
        };
        if (current.selected === selected) return prev;

        const selectedIndices = Object.entries(prev)
          .filter(([key, draft]) => {
            const index = Number(key);
            if (index === pageIndex) return selected;
            return draft.selected;
          })
          .map(([key]) => Number(key));
        if (selected && !selectedIndices.includes(pageIndex)) {
          selectedIndices.push(pageIndex);
        }

        return withSelectionStoreyDefaults(
          {
            ...prev,
            [pageIndex]: { ...current, selected },
          },
          selectedIndices,
          resolveDefaultAssignment(),
          storeyBaseLevel,
          storeyLevelCount,
        );
      });
    },
    [resolveDefaultAssignment, storeyBaseLevel, storeyLevelCount],
  );

  /**
   * Toggle page selection for import (checkbox).
   */
  const toggleSelected = useCallback(
    (pageIndex: number) => {
      setPageSelected(pageIndex, !getDraft(pageIndex).selected);
    },
    [getDraft, setPageSelected],
  );

  /**
   * Select every page in the PDF (checkbox bulk action).
   */
  const selectAllPages = useCallback(() => {
    setPdfImportPageDrafts((prev) => {
      const selectedIndices = thumbnails.map((thumb) => thumb.pageIndex);
      const seeded: PdfImportPageDrafts = { ...prev };
      for (const pageIndex of selectedIndices) {
        seeded[pageIndex] = prev[pageIndex] ?? {
          selected: false,
          assignment: resolveDefaultAssignment(),
        };
      }
      return withSelectionStoreyDefaults(
        seeded,
        selectedIndices,
        resolveDefaultAssignment(),
        storeyBaseLevel,
        storeyLevelCount,
      );
    });
  }, [resolveDefaultAssignment, storeyBaseLevel, storeyLevelCount, thumbnails]);

  /**
   * Clear all page checkboxes (keeps assignment drafts).
   */
  const clearPageSelection = useCallback(() => {
    setPdfImportPageDrafts((prev) => {
      const next: PdfImportPageDrafts = {};
      for (const [key, draft] of Object.entries(prev)) {
        next[Number(key)] = { ...draft, selected: false };
      }
      return next;
    });
  }, []);

  /**
   * Jump the continuous viewer to a page (thumbnail click).
   */
  const focusPage = useCallback((pageIndex: number) => {
    setFocusedPageIndex(pageIndex);
    setScrollRequestPageIndex(pageIndex);
    setScrollRequestId((id) => id + 1);
  }, []);

  /**
   * Open select/deselect menu at the pointer (thumbnail or large preview).
   */
  const openThumbContextMenu = useCallback(
    (event: React.MouseEvent, pageIndex: number) => {
      event.preventDefault();
      event.stopPropagation();
      focusPage(pageIndex);
      setThumbContextMenu({
        pageIndex,
        position: { top: event.clientY, left: event.clientX },
      });
    },
    [focusPage],
  );

  // Keep the active thumbnail visible in the left rail while scrolling the stack.
  useEffect(() => {
    if (wizardStep !== 1 || focusedPageIndex === undefined) return;
    const item = thumbItemRefs.current.get(focusedPageIndex);
    // Instant — smooth here cascaded focus chrome through intermediate thumbs.
    item?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  }, [focusedPageIndex, wizardStep]);

  // Arrow Up / Down moves the preview focus through the thumbnail rail.
  useEffect(() => {
    if (wizardStep !== 1 || thumbnails.length === 0) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== KeyboardKeys.ARROW_UP &&
        event.key !== KeyboardKeys.ARROW_DOWN
      ) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      const currentIndex =
        focusedPageIndex === undefined
          ? 0
          : thumbnails.findIndex(
              (thumb) => thumb.pageIndex === focusedPageIndex,
            );
      const safeIndex = currentIndex < 0 ? 0 : currentIndex;
      const delta = event.key === KeyboardKeys.ARROW_DOWN ? 1 : -1;
      const nextIndex = Math.min(
        thumbnails.length - 1,
        Math.max(0, safeIndex + delta),
      );
      const next = thumbnails[nextIndex];
      if (!next || next.pageIndex === focusedPageIndex) return;
      event.preventDefault();
      focusPage(next.pageIndex);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusPage, focusedPageIndex, thumbnails, wizardStep]);

  /**
   * Set the start storey for a page (same model as the right PDF panel).
   */
  const setAssignmentStartLevel = useCallback(
    (pageIndex: number, startLevel: number) => {
      const level = Math.min(
        storeyLevelCount,
        Math.max(1, Math.floor(startLevel)),
      );
      setPdfImportPageDrafts((prev) => {
        const current = prev[pageIndex] ?? {
          selected: true,
          assignment: resolveDefaultAssignment(),
        };
        return {
          ...prev,
          [pageIndex]: {
            ...current,
            selected: true,
            assignment: blueprintStoreyAssignmentFromStartLevel(level),
          },
        };
      });
    },
    [resolveDefaultAssignment, storeyLevelCount],
  );

  const selectedPages = useMemo(
    () => thumbnails.filter((thumb) => getDraft(thumb.pageIndex).selected),
    [getDraft, thumbnails],
  );

  // Measure step-2 thumbs after paint (cached bitmaps may skip onLoad).
  useLayoutEffect(() => {
    if (wizardStep !== 2) return;
    setAssignThumbHeights((prev) => {
      let next = prev;
      for (const thumb of selectedPages) {
        const node = assignThumbImageRefs.current.get(thumb.pageIndex);
        const height = node?.offsetHeight ?? 0;
        if (height <= 0 || next[thumb.pageIndex] === height) continue;
        if (next === prev) next = { ...prev };
        next[thumb.pageIndex] = height;
      }
      return next;
    });
  }, [selectedPages, wizardStep]);

  const allPagesSelected =
    thumbnails.length > 0 && selectedPages.length === thumbnails.length;

  /**
   * Toggle select-all: clear when everything is selected, otherwise select all.
   */
  const toggleSelectAllPages = useCallback(() => {
    if (allPagesSelected) {
      clearPageSelection();
      return;
    }
    selectAllPages();
  }, [allPagesSelected, clearPageSelection, selectAllPages]);

  const selectedReadyPages = useMemo(
    () =>
      selectedPages
        .map((thumb) => ({
          pageIndex: thumb.pageIndex,
          draft: getDraft(thumb.pageIndex),
          thumb,
        }))
        .filter(({ draft }) => isAssignmentReady(draft.assignment)),
    [getDraft, selectedPages],
  );

  /**
   * Assign is required only when several pages are selected (and the project
   * has more than one storey). Driven by selection, not the PDF page count.
   */
  const needsAssignStep = resolvePdfImportWizardPlan({
    pdfPageCount: thumbnails.length,
    selectedPageCount: selectedPages.length,
    storeyLevelCount,
    buildingCount,
  }).showAssignStep;

  /**
   * Go back one wizard step (or close when assign was the only step).
   */
  const handleBack = useCallback(() => {
    if (loading) return;
    switch (wizardStep) {
      case 1:
        return;
      case 2:
        if (skippedSelectStep) {
          onClose();
          return;
        }
        setPdfImportWizardStep(1);
        return;
      default:
        return assertUnreachable(wizardStep);
    }
  }, [loading, onClose, skippedSelectStep, wizardStep]);

  /**
   * Place the selected pages. Scale is either inherited from an already
   * calibrated sheet, or measured on the Design map after placing.
   */
  const handlePlace = useCallback(
    async (mode: PdfImportPlaceMode) => {
      if (loading) return;
      if (selectedReadyPages.length === 0) return;
      if (mode === 'inherit' && !canInheritCalibration) return;

      if (!loadedPdf) return;
      setLoading(true);
      try {
        const inheritedScale =
          mode === 'inherit' && matchScaleFrom
            ? getBlueprintDrawingScaleRatio(matchScaleFrom)
            : undefined;
        const underlays: BlueprintUnderlay[] = [];
        for (const { pageIndex, draft, thumb } of selectedReadyPages) {
          // Bake a Mapbox-ready JPEG only at place time — steps 1–2 keep the live PDF.
          const raster = await rasterizePdfPageFromDoc(
            loadedPdf.doc,
            pageIndex,
          );
          const created: BlueprintUnderlay = {
            ...createBlueprintUnderlay({
              imageDataUrl: raster.imageDataUrl,
              pageIndex: raster.pageIndex,
              fileName: file.name,
              label: thumb.pageName,
              pixelWidth: raster.pixelWidth,
              pixelHeight: raster.pixelHeight,
              paperWidthMm: raster.paperWidthMm,
              paperHeightMm: raster.paperHeightMm,
              drawingScaleRatio: inheritedScale,
              storeyAssignment: draft.assignment,
            }),
            ...(buildingId ? { buildingId } : {}),
          };
          underlays.push(
            mode === 'inherit' && matchScaleFrom?.calibration?.primary
              ? inheritBlueprintCalibrationFromSource(created, matchScaleFrom)
              : created,
          );
        }

        const placed = underlays;

        await onPlaceUnderlays(placed);
        // Keep loading true through the exit fade so sticky UI stays frozen.
        onClose();
      } catch (error: unknown) {
        reportDesignPersistError(error, errorHandler);
        setLoading(false);
      }
    },
    [
      buildingId,
      canInheritCalibration,
      errorHandler,
      file,
      loadedPdf,
      loading,
      matchScaleFrom,
      onClose,
      onPlaceUnderlays,
      selectedReadyPages,
    ],
  );

  // Nothing to select and nothing to assign — place without showing a step.
  useEffect(() => {
    if (!autoPlaceRef.current) return;
    if (loading || !loadedPdf || selectedReadyPages.length === 0) return;
    autoPlaceRef.current = false;
    void handlePlace(finishImportMode);
  }, [finishImportMode, handlePlace, loadedPdf, loading, selectedReadyPages]);

  /**
   * Advance the wizard: select → (assign if several pages selected) → place.
   */
  const handleContinue = useCallback(() => {
    switch (wizardStep) {
      case 1: {
        if (selectedPages.length === 0) return;
        // Several pages: always confirm storey mapping before placing.
        if (needsAssignStep) {
          setPdfImportWizardStep(2);
          return;
        }
        void handlePlace(finishImportMode);
        return;
      }
      case 2:
        void handlePlace(finishImportMode);
        return;
      default:
        return assertUnreachable(wizardStep);
    }
  }, [
    finishImportMode,
    handlePlace,
    needsAssignStep,
    selectedPages.length,
    wizardStep,
  ]);

  const handleCancel = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const handleDialogClose = useCallback(
    (_event: object, reason?: string) => {
      void reason;
      handleCancel();
    },
    [handleCancel],
  );

  return {
    open,
    file,
    storeys,
    buildings,
    buildingId,
    setBuildingId,
    wizardStep,
    loading,
    loadingThumbnails,
    thumbContextMenu,
    setThumbContextMenu,
    selectThumbImageRefs,
    assignThumbImageRefs,
    assignThumbHeights,
    setAssignThumbHeights,
    loadedPdf,
    thumbnails,
    pageDrafts,
    focusedPageIndex,
    setFocusedPageIndex,
    scrollRequestPageIndex,
    scrollRequestId,
    thumbItemRefs,
    canInheritCalibration,
    finishImportMode,
    selectedPages,
    selectedReadyPages,
    allPagesSelected,
    getDraft,
    toggleSelected,
    toggleSelectAllPages,
    setPageSelected,
    setAssignmentStartLevel,
    storeyLevelCount,
    focusPage,
    openThumbContextMenu,
    handleBack,
    handleContinue,
    handlePlace,
    handleCancel,
    handleDialogClose,
  };
};
